import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useDatabase } from '@/contexts/DatabaseContext';
import { useToast } from '@/contexts/ToastContext';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { exportTrucksToXLSX, exportTrucksToPDF, exportDriversToXLSX } from '@/utils/export';

type FilterStatus = 'all' | 'Bon' | 'En panne' | 'Réparé';

const ITEMS_PER_PAGE = 10;

export default function Home() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { trucks, drivers } = useDatabase();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setExportOpen(false);
      }
    }
    if (exportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportOpen]);

  const stats = useMemo(() => {
    const total = trucks.length;
    const broken = trucks.filter((t) => t.generalStatus === 'En panne').length;
    const good = trucks.filter((t) => t.generalStatus === 'Bon').length;
    const repaired = trucks.filter((t) => t.generalStatus === 'Réparé').length;
    return { total, broken, good, repaired };
  }, [trucks]);

  const filteredTrucks = useMemo(() => {
    return trucks.filter((truck) => {
      const matchesSearch =
        searchQuery === '' ||
        truck.doorNumber.toUpperCase().includes(('SILY-' + searchQuery).toUpperCase());

      const matchesFilter =
        filterStatus === 'all' || truck.generalStatus === filterStatus;

      return matchesSearch && matchesFilter;
    });
  }, [trucks, searchQuery, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredTrucks.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  const paginatedTrucks = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredTrucks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTrucks, safePage]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'OK':
        return 'bg-primary-100 text-primary-800';
      case 'En attente':
        return 'bg-accent-100 text-accent-800';
      case 'Terminé':
        return 'bg-primary-100 text-primary-800';
      case 'Bon':
        return 'bg-primary-100 text-primary-800';
      case 'En panne':
        return 'bg-accent-100 text-accent-800';
      case 'Réparé':
        return 'bg-primary-100 text-primary-800';
      case 'En cours':
        return 'bg-secondary-100 text-secondary-800';
      default:
        return 'bg-background-100 text-foreground-600';
    }
  };

  const getRepairLabel = (status: string) => {
    switch (status) {
      case 'OK':
        return t('ok');
      case 'En attente':
        return t('waiting');
      case 'Terminé':
        return t('done');
      default:
        return status;
    }
  };

  const getGeneralLabel = (status: string) => {
    switch (status) {
      case 'Bon':
        return t('good');
      case 'En panne':
        return t('broken');
      case 'Réparé':
        return t('repaired');
      default:
        return status;
    }
  };

  const filterOptions: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: t('all') },
    { key: 'Bon', label: t('good') },
    { key: 'En panne', label: t('broken') },
    { key: 'Réparé', label: t('repaired') },
  ];

  const visiblePages = useMemo(() => {
    if (totalPages <= 5) {
      const pageNumbers: number[] = [];
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
      return pageNumbers;
    }
    const start = Math.max(1, safePage - 2);
    const end = Math.min(totalPages, safePage + 2);
    const pages: (number | '...')[] = [];
    if (start > 1) pages.push(1);
    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('...');
    if (end < totalPages) pages.push(totalPages);
    return pages;
  }, [safePage, totalPages]);

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs md:text-sm font-medium text-foreground-500">
                {t('totalTrucks')}
              </span>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-truck-line text-secondary-600 text-sm md:text-lg" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              {stats.total}
            </p>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs md:text-sm font-medium text-foreground-500">
                {t('brokenTrucks')}
              </span>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-alert-line text-accent-600 text-sm md:text-lg" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-accent-700 font-heading">
              {stats.broken}
            </p>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs md:text-sm font-medium text-foreground-500">
                {t('goodTrucks')}
              </span>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-check-line text-primary-600 text-sm md:text-lg" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-primary-700 font-heading">
              {stats.good}
            </p>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs md:text-sm font-medium text-foreground-500">
                {t('repairedTrucks')}
              </span>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-tools-line text-secondary-600 text-sm md:text-lg" />
              </div>
            </div>
            <p className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              {stats.repaired}
            </p>
          </div>
        </div>

        {/* Search, Filters and Export */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-foreground-400" />
              </div>
              <div className="flex items-center w-full rounded-lg border border-background-300 bg-background-50 focus-within:ring-2 focus-within:ring-primary-400 focus-within:border-primary-400 overflow-hidden">
                <span className="pl-10 pr-0 py-2.5 text-sm font-medium text-foreground-700 bg-background-50 select-none whitespace-nowrap">
                  SILY-
                </span>
                <input
                  type="text"
                  placeholder={t('searchDoorNumber')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 py-2.5 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 bg-transparent focus:outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {filterOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setFilterStatus(opt.key)}
                  className={`px-3 py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                    filterStatus === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="relative" ref={exportRef}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="px-3 py-2 rounded-lg text-xs md:text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-download-2-line" />
                {t('export')}
                <i className="ri-arrow-down-s-line text-xs" />
              </button>

              {exportOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-background-50 rounded-lg border border-background-200 shadow-lg z-50 py-1">
                  <button
                    onClick={() => {
                      exportTrucksToXLSX(trucks, t);
                      showToast('Export Excel des camions réussi.', 'success');
                      setExportOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-file-excel-2-line mr-2 text-primary-600" />
                    {t('exportTrucksXLSX')}
                  </button>
                  <button
                    onClick={() => {
                      exportDriversToXLSX(drivers, t);
                      showToast('Export Excel des chauffeurs réussi.', 'success');
                      setExportOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-file-excel-2-line mr-2 text-primary-600" />
                    {t('exportDriversXLSX')}
                  </button>
                  <div className="border-t border-background-100 my-1" />
                  <button
                    onClick={() => {
                      exportTrucksToPDF(trucks, t);
                      showToast('Export PDF réussi.', 'success');
                      setExportOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-file-pdf-2-line mr-2 text-accent-600" />
                    {t('exportPDF')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-background-100 border-b border-background-200">
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('doorNumber')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('licensePlate')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">
                    {t('reportedProblem')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden xl:table-cell">
                    {t('breakdownDate')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('repairStatus')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                    {t('driver')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">
                    {t('contact')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider text-right">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {paginatedTrucks.map((truck) => (
                  <tr
                    key={truck.id}
                    className="hover:bg-background-100/50 transition-colors cursor-pointer"
                    onDoubleClick={() => navigate(`/trucks/${truck.id}`)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-sm text-foreground-900">
                        {truck.doorNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {truck.licensePlate}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(truck.generalStatus)}`}
                      >
                        {getGeneralLabel(truck.generalStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 hidden lg:table-cell max-w-[200px] truncate">
                      {truck.reportedProblem || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 hidden xl:table-cell">
                      {truck.breakdownDate
                        ? new Date(truck.breakdownDate).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(truck.repairStatus)}`}
                      >
                        {getRepairLabel(truck.repairStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 hidden md:table-cell">
                      {truck.driverName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 hidden sm:table-cell">
                      {truck.driverContact || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/trucks/${truck.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-background-200 text-foreground-500 hover:text-foreground-800 transition-colors"
                      >
                        <i className="ri-eye-line text-base" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTrucks.length === 0 && (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-search-line text-foreground-400 text-xl" />
              </div>
              <p className="text-sm text-foreground-500">{t('noResults')}</p>
            </div>
          )}

          {filteredTrucks.length > 0 && (
            <div className="px-4 py-3 bg-background-100 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-xs text-foreground-500">
                {t('page')} {safePage} {t('of')} {totalPages} — {filteredTrucks.length} / {trucks.length} {t('trucks').toLowerCase()}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>

                {visiblePages.map((page, idx) =>
                  page === '...' ? (
                    <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs text-foreground-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page as number)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        safePage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}